// SalesContributionChart.tsx
import React, { useState, useEffect, useRef } from "react";
import classNames from "classnames";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Title,
  Tooltip,
  Legend,
  type ChartOptions,
  type TooltipItem,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { useSelector } from "react-redux";

import { useAppDispatch } from "../../redux";
import { getSalesContributionAction } from "../../redux/dashboard/analytics.slice";

import ExportData from "./export-data";

import Loader from "../../components/loader";
import NoData from "../../components/no-data";

import { useAnalyticsFilter } from "../../context/AnalyticsFilterContext";

import { handleExportPdf, handleExportPng } from "../../utils/functions";

interface SalesContributionData {
  name: string;
  konvas: string;
  sale: string;
}

// 1) Register the Chart.js components you need
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Title,
  Tooltip,
  Legend
);

const dateRange = [
  { name: "12 months", value: "12month" },
  { name: "30 days", value: "30days" },
  { name: "7days", value: "7days" },
  { name: "24 hours", value: "24hours" },
  { name: "Today", value: "12hours" },
];

const options: ChartOptions<"line"> = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    title: {
      display: false,
      text: "Contribution of Konvas AI to sales",
    },
    legend: {
      position: "top", // TS knows 'top' is valid here
      labels: {
        boxWidth: 12,
        padding: 20,
      },
    },
    tooltip: {
      callbacks: {
        label: (ctx: TooltipItem<"line">) => {
          const val = ctx.parsed.y as number;
          return `${ctx.dataset.label}: $${val.toLocaleString()}`;
        },
      },
    },
  },
  scales: {
    x: {
      grid: { display: false },
    },
    y: {
      beginAtZero: true,
      grid: { color: "#e0e0e0" },
      ticks: {
        callback: (v) => {
          const n = Number(v);
          return n === 0 ? "$0" : `$${n}`;
        },
      },
    },
  },
};

const SalesContributionChart: React.FC = () => {
  const dispatch = useAppDispatch();

  const { isFetchingSalesContribution, salesContribution } = useSelector(
    (state: any) => state.analyticsSlice
  );

  const { duration } = useAnalyticsFilter();

  const [currentDateRange, setCurrentDateRange] = useState(dateRange[0]?.value);

  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    dispatch(getSalesContributionAction({ range: currentDateRange }));
  }, [currentDateRange]);

  useEffect(() => {
    if (duration) setCurrentDateRange(duration);
  }, [duration]);

  const exportOptions = [
    {
      name: "PDF",
      action: () =>
        chartRef.current &&
        handleExportPdf(chartRef.current, "konvasAI-sales-contribution.pdf"),
    },
    {
      name: "Image(PNG)",
      action: () =>
        chartRef.current &&
        handleExportPng(chartRef.current, "konvasAI-sales-contribution.png"),
    },
  ];

  // 2) X-axis labels
  const labels = salesContribution?.map(
    (data: SalesContributionData) => data?.name
  );

  const overallSales = salesContribution?.map((data: SalesContributionData) =>
    Number(data?.sale?.replace(/[^0-9.-]+/g, ""))
  );

  const aiContribution = salesContribution?.map((data: SalesContributionData) =>
    Number(data?.konvas.replace(/[^0-9.-]+/g, ""))
  );

  const data = {
    labels,
    datasets: [
      {
        label: "Overall sales",
        data: overallSales,
        borderColor: "#006AA0", // exact from legend dot
        borderWidth: 2,
        pointRadius: 3,
        tension: 0.3,
        fill: false,
      },
      {
        label: "Konvas AI contribution to sales",
        data: aiContribution,
        borderColor: "#00A5E9", // exact from legend dot
        borderWidth: 2,
        pointRadius: 3,
        tension: 0.3,
        fill: "-1", // fill down to the previous dataset
        backgroundColor: "#E5EEF3", // exact interior fill color
      },
    ],
  };

  return (
    <div className="chart_section">
      <div className="chart_section_header">
        <p>Contribution of Konvas AI to sales</p>
        <ExportData size="small" exportOptions={exportOptions} />
      </div>
      <div className="date_range_select">
        {dateRange.map((range: { name: string; value: string }) => (
          <div
            className={classNames("date_range", {
              active: range?.value === currentDateRange,
            })}
            key={range?.value}
            onClick={() => setCurrentDateRange(range?.value)}
          >
            <p>{range?.name}</p>
          </div>
        ))}
      </div>
      {isFetchingSalesContribution ? (
        <Loader />
      ) : !salesContribution || salesContribution?.length === 0 ? (
        <NoData text="No data" />
      ) : (
        <div className="chart_box" ref={chartRef}>
          <Line data={data} options={options} />
        </div>
      )}
    </div>
  );
};

export default SalesContributionChart;
